import React from 'react'
import { FileSpreadsheet as CsvIcon, FileText as PdfIcon } from 'lucide-react'
import type { ReportData } from './types'

interface Props {
  data: ReportData
  onExportCSV: () => void
  onExportPDF: () => void
}

export const ReportTable: React.FC<Props> = ({ data, onExportCSV, onExportPDF }) => (
  <div className="space-y-4">
    {/* Export bar */}
    <div className="flex justify-end gap-2">
      <button
        onClick={onExportCSV}
        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
      >
        <CsvIcon size={15} />
        Export CSV
      </button>
      <button
        onClick={onExportPDF}
        className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors text-sm font-medium"
      >
        <PdfIcon size={15} />
        Export PDF
      </button>
    </div>

    {/* Summary cards */}
    {data.summary && Object.keys(data.summary).length > 0 && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(data.summary).map(([key, value]) => (
          <div key={key} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 capitalize">
              {key.replace(/_/g, ' ')}
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-white">
              {value !== null && value !== undefined ? String(value) : '—'}
            </div>
          </div>
        ))}
      </div>
    )}

    {/* Data table */}
    {data.rows.length === 0 ? (
      <div className="text-center py-16 text-gray-500 dark:text-gray-400">
        No data for this period.
      </div>
    ) : (
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              {data.headers.map((header, idx) => (
                <th
                  key={idx}
                  className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap border-b border-gray-200 dark:border-gray-700"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {data.rows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
              >
                {row.map((cell, cellIdx) => (
                  <td key={cellIdx} className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {cell !== null && cell !== undefined ? String(cell) : '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
)
